import { getDatabase, ref, get, set, remove, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { BaseType } from '../types/Enums';
import { LibraryTemplate, LibraryTemplateInput } from '../types/LibraryTemplate';
import { LibraryConfig, pickFields } from '../library/LibraryConfig';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { generateSlug } from '../Helpers/slugUtils';
import { recordActivity } from './activityApi';

export type LibraryApiConfig<F extends object> = Pick<
    LibraryConfig<F>,
    'path' | 'fieldKeys' | 'parseFields' | 'validateFields'
>;

export interface LibraryBatchItem<F extends object> {
    id: string;
    data: LibraryTemplateInput<F>;
    isUpdate: boolean;
}

/**
 * Generic API for a superadmin template library stored at `config.path/{id}`.
 * The shared fields (translations, baseTypes, metadata) are handled here;
 * the library-specific fields are parsed and validated by the config.
 *
 * Read operations: any authenticated user
 * Write operations: super-admin only (enforced in UI and DB rules)
 */
export class LibraryApi<F extends object> {
    private db = getDatabase();

    constructor(private readonly config: LibraryApiConfig<F>) {}

    private get path(): string {
        return this.config.path;
    }

    private templateRef(id: string) {
        return ref(this.db, `${this.path}/${id}`);
    }

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Parse and validate Firebase data before type assertion.
     * Returns null for invalid data (logs warning and skips).
     */
    private parseTemplate(data: unknown, key: string): LibraryTemplate<F> | null {
        if (!data || typeof data !== 'object') {
            console.warn(`Invalid library template data for key ${key}: not an object`);
            return null;
        }

        const d = data as Record<string, unknown>;

        if (!d.translations || typeof d.translations !== 'object') {
            console.warn(`Invalid library template ${key}: missing translations`);
            return null;
        }
        const translations = d.translations as Record<string, unknown>;
        if (typeof translations.sv !== 'string') {
            console.warn(`Invalid library template ${key}: missing translations.sv`);
            return null;
        }

        // Firebase drops empty arrays on write, so a template with no base
        // types comes back without the key. Treat that as an empty list.
        if (d.baseTypes !== undefined && !Array.isArray(d.baseTypes)) {
            console.warn(`Invalid library template ${key}: baseTypes must be an array`);
            return null;
        }

        const fields = this.config.parseFields(d, key);
        if (!fields) {
            return null;
        }

        return {
            id: key,
            translations: d.translations as { sv: string; [key: string]: string },
            baseTypes: (d.baseTypes as BaseType[] | undefined) ?? [],
            createdBy: (d.createdBy as string) || '',
            createdAt: (d.createdAt as string) || '',
            updatedBy: d.updatedBy as string | undefined,
            updatedAt: d.updatedAt as string | undefined,
            ...fields,
        };
    }

    private validate(input: Partial<LibraryTemplateInput<F>>, mode: 'full' | 'partial'): void {
        if (mode === 'full' || input.translations !== undefined) {
            if (!input.translations?.sv || input.translations.sv.trim() === '') {
                throw new Error('Namn är obligatoriskt');
            }
        }
        // baseTypes can be empty - no validation required
        this.config.validateFields(pickFields<F>(input, this.config.fieldKeys), mode);
    }

    // ============================================================
    // Read Operations
    // ============================================================

    /**
     * Get all library templates, sorted by Swedish name.
     * Templates are filtered client-side by baseType as needed.
     */
    async getAllTemplates(): Promise<LibraryTemplate<F>[]> {
        const snapshot = await get(ref(this.db, this.path));
        if (!snapshot.exists()) {
            return [];
        }

        const templates: LibraryTemplate<F>[] = [];
        const data = snapshot.val() as Record<string, unknown>;

        for (const [key, value] of Object.entries(data)) {
            const template = this.parseTemplate(value, key);
            if (template) {
                templates.push(template);
            }
        }

        templates.sort((a, b) => a.translations.sv.localeCompare(b.translations.sv, 'sv'));

        return templates;
    }

    // ============================================================
    // Write Operations (super-admin only)
    // ============================================================

    /**
     * Create a new library template.
     * Uses slugified name as the key; fails if that key is already taken
     * so an existing template is never silently overwritten.
     * @returns The ID of the newly created template
     */
    async createTemplate(input: LibraryTemplateInput<F>): Promise<string> {
        const user = this.getCurrentUser();

        this.validate(input, 'full');

        const key = generateSlug(input.translations.sv);
        if (!key) {
            throw new Error('Kunde inte skapa mall-ID från namn');
        }

        const existing = await get(this.templateRef(key));
        if (existing.exists()) {
            throw new Error('En mall med detta namn finns redan');
        }

        await set(this.templateRef(key), {
            translations: input.translations,
            baseTypes: input.baseTypes,
            ...pickFields<F>(input, this.config.fieldKeys),
            createdBy: user.uid,
            createdAt: formatDateForStorage(new Date()),
        });
        // Superadmin template libraries are platform config
        recordActivity('settings.updated', { targetKey: key, label: input.translations.sv });

        return key;
    }

    /**
     * Update an existing library template.
     * Uses Firebase update() for partial updates without requiring a fetch first.
     */
    async updateTemplate(id: string, updates: Partial<LibraryTemplateInput<F>>): Promise<void> {
        const user = this.getCurrentUser();

        if (!id) {
            throw new Error('Mall-ID saknas');
        }

        this.validate(updates, 'partial');

        const payload: Record<string, unknown> = {
            ...pickFields<F>(updates, this.config.fieldKeys),
            updatedBy: user.uid,
            updatedAt: formatDateForStorage(new Date()),
        };
        if (updates.baseTypes !== undefined) {
            payload.baseTypes = updates.baseTypes;
        }
        // Per-language paths so other translations survive a rename.
        if (updates.translations !== undefined) {
            for (const [lang, text] of Object.entries(updates.translations)) {
                payload[`translations/${lang}`] = text;
            }
        }

        await update(this.templateRef(id), payload);
        recordActivity('settings.updated', { targetKey: id, label: updates.translations?.sv });
    }

    /**
     * Batch upsert (create or update) multiple templates in a single Firebase write.
     * Much faster than individual writes for bulk imports.
     *
     * @returns Object with counts of created and updated templates
     */
    async batchUpsertTemplates(items: LibraryBatchItem<F>[]): Promise<{ created: number; updated: number }> {
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());

        const updates: Record<string, unknown> = {};
        let created = 0;
        let updated = 0;

        for (const { id, data, isUpdate } of items) {
            if (!id) continue;

            this.validate(data, 'full');

            const fields = pickFields<F>(data, this.config.fieldKeys) as Record<string, unknown>;

            if (isUpdate) {
                // Write per field so createdBy/createdAt (and any other
                // existing keys) survive; a whole-object write would replace the node.
                const perField: Record<string, unknown> = {
                    ...fields,
                    baseTypes: data.baseTypes,
                    updatedBy: user.uid,
                    updatedAt: now,
                };
                for (const [field, value] of Object.entries(perField)) {
                    updates[`${this.path}/${id}/${field}`] = value;
                }
                for (const [lang, text] of Object.entries(data.translations)) {
                    updates[`${this.path}/${id}/translations/${lang}`] = text;
                }
                updated++;
            } else {
                updates[`${this.path}/${id}`] = {
                    translations: data.translations,
                    baseTypes: data.baseTypes,
                    ...fields,
                    createdBy: user.uid,
                    createdAt: now,
                };
                created++;
            }
        }

        if (Object.keys(updates).length > 0) {
            await update(ref(this.db), updates);
            recordActivity('settings.imported', {});
        }

        return { created, updated };
    }

    /**
     * Replace the ENTIRE library with the given templates in one atomic write:
     * templates not in `items` are removed and every item is written as new
     * (createdBy/createdAt are the current user and now, nothing is carried
     * over from an existing template with the same id). An empty list empties
     * the library. Irreversible — callers must confirm with the user first.
     */
    async replaceAllTemplates(items: Array<Pick<LibraryBatchItem<F>, 'id' | 'data'>>): Promise<void> {
        const user = this.getCurrentUser();
        const now = formatDateForStorage(new Date());

        const library: Record<string, unknown> = {};

        for (const { id, data } of items) {
            if (!id) {
                throw new Error('Mall-ID saknas');
            }

            this.validate(data, 'full');

            library[id] = {
                translations: data.translations,
                baseTypes: data.baseTypes,
                ...pickFields<F>(data, this.config.fieldKeys),
                createdBy: user.uid,
                createdAt: now,
            };
        }

        await set(ref(this.db, this.path), library);
        recordActivity('settings.imported', {});
    }

    /**
     * Delete a library template.
     */
    async deleteTemplate(id: string): Promise<void> {
        if (!id) {
            throw new Error('Mall-ID saknas');
        }

        const snapshot = await get(this.templateRef(id));
        if (!snapshot.exists()) {
            throw new Error('Mallen hittades inte');
        }

        await remove(this.templateRef(id));
        recordActivity('settings.updated', { targetKey: id, label: this.parseTemplate(snapshot.val(), id)?.translations.sv });
    }

    /**
     * Delete ALL library templates by removing the entire library node.
     * Irreversible — there is no soft-delete or recovery. Callers must
     * confirm with the user before invoking this.
     *
     * @returns The number of templates that were deleted.
     */
    async deleteAllTemplates(): Promise<number> {
        this.getCurrentUser();

        const snapshot = await get(ref(this.db, this.path));
        const count = snapshot.exists()
            ? Object.keys(snapshot.val() as Record<string, unknown>).length
            : 0;

        await remove(ref(this.db, this.path));
        recordActivity('settings.updated', {});

        return count;
    }
}
